import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "../components/AppIcon";
import Button from "../components/ui/Button";
import {
  getBusinessCredits,
  listBusinessCreditTransactions,
} from "../api/client";

// Credits — the business credit model is a single per-organization WALLET.
// Buying tops it up; each interview attempt reserves one credit directly from
// the wallet, and no-shows / expiries release it back. There are no per-batch
// pools (that is the institution model) — so this page shows the wallet balance,
// lifetime flow, and the append-only ledger, with buying handled on Buy Credits.

const fmt = (n) => Number(n ?? 0).toLocaleString("en-IN");

const TXN_LABEL = {
  purchase: "Purchased",
  reserve: "Reserved",
  release: "Released",
  refund: "Refunded",
  adjustment: "Adjusted",
};

function formatDateTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function StatCard({ icon, label, value, hint, accent }) {
  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">{label}</span>
        <span
          className={
            "flex h-8 w-8 items-center justify-center rounded-lg " +
            (accent || "bg-primary/10 text-primary")
          }
        >
          <Icon name={icon} size={16} />
        </span>
      </div>
      <div className="mt-3 text-2xl font-semibold text-foreground">{value}</div>
      {hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>}
    </div>
  );
}

export default function Credits() {
  const navigate = useNavigate();
  const [wallet, setWallet] = useState(null);
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const [totals, ledger] = await Promise.all([
        getBusinessCredits(),
        listBusinessCreditTransactions({ limit: 100 }),
      ]);
      setWallet(totals);
      setTxns(ledger.transactions || []);
    } catch (err) {
      setLoadError(err?.error || "Could not load credits.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const walletBalance = wallet?.walletBalance ?? 0;

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Credits</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            One credit covers one candidate interview. Inviting a candidate
            reserves a credit; no-shows and expired invites release it back.
          </p>
        </div>
        <Button
          size="sm"
          iconName="CreditCard"
          onClick={() => navigate("/business/buy-credits")}
        >
          Buy credits
        </Button>
      </div>

      {/* Wallet + lifetime flow */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          icon="Wallet"
          label="Wallet balance"
          value={loading ? "…" : fmt(walletBalance)}
          hint="Available to invite candidates"
          accent="bg-success/15 text-success"
        />
        <StatCard
          icon="Clock"
          label="In use"
          value={loading ? "…" : fmt(wallet?.activeReservations)}
          hint="Reserved by open invites"
        />
        <StatCard
          icon="ShoppingCart"
          label="Lifetime purchased"
          value={loading ? "…" : fmt(wallet?.lifetimePurchased)}
          hint="Total credits bought"
        />
        <StatCard
          icon="RotateCcw"
          label="Lifetime released"
          value={loading ? "…" : fmt(wallet?.lifetimeReleased)}
          hint="Returned from no-shows"
        />
      </div>

      {/* Load error */}
      {loadError && (
        <div className="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          <Icon name="AlertCircle" size={16} />
          <span>{loadError}</span>
          <button onClick={load} className="ml-auto font-medium underline">
            Retry
          </button>
        </div>
      )}

      {/* Low-balance nudge */}
      {!loading && !loadError && walletBalance <= 0 && (
        <div className="flex items-start gap-3 rounded-xl border border-warning/30 bg-warning/10 px-4 py-3.5">
          <Icon name="AlertTriangle" size={18} className="mt-0.5 flex-shrink-0 text-warning" />
          <div className="flex-1 text-sm">
            <p className="font-medium text-foreground">Your wallet is empty.</p>
            <p className="text-muted-foreground">
              Buy credits before you invite candidates — each invite reserves one.
            </p>
          </div>
          <Button
            size="sm"
            iconName="CreditCard"
            onClick={() => navigate("/business/buy-credits")}
          >
            Buy credits
          </Button>
        </div>
      )}

      {/* Ledger */}
      <div className="rounded-xl border border-border bg-card">
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <h2 className="text-sm font-semibold text-foreground">Credit activity</h2>
          <Icon name="History" size={16} className="text-muted-foreground" />
        </div>
        {loading ? (
          <div className="space-y-2 p-4">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="h-12 animate-pulse rounded-lg bg-muted/50" />
            ))}
          </div>
        ) : txns.length === 0 ? (
          <div className="px-5 py-12 text-center text-sm text-muted-foreground">
            No credit activity yet. Buying credits and inviting candidates will
            show up here.
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {txns.map((t) => {
              const positive = t.amount > 0;
              return (
                <li key={t.id} className="flex items-center justify-between gap-4 px-5 py-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-foreground">
                      {TXN_LABEL[t.transactionType] || t.transactionType}
                      {t.reason ? (
                        <span className="font-normal text-muted-foreground"> · {t.reason}</span>
                      ) : null}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {formatDateTime(t.createdAt)}
                    </div>
                  </div>
                  <div className="flex flex-shrink-0 items-center gap-4">
                    <span
                      className={
                        "text-sm font-semibold tabular-nums " +
                        (positive ? "text-success" : "text-destructive")
                      }
                    >
                      {positive ? "+" : ""}
                      {t.amount}
                    </span>
                    {t.balanceAfter != null && (
                      <span className="w-20 text-right text-xs text-muted-foreground tabular-nums">
                        bal {fmt(t.balanceAfter)}
                      </span>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
